<template>
  <div class="page page--how-far">
    <Head>
      <title>{{ $t("how-far-title") }} {{ $t("how-far-subtitle") }}</title>
      <body class="site--how-far" />
      <meta name="description" :content="$t('page.how-far.description')" />
      <meta name="og:title" :content="$t('how-far-title')" />
      <meta name="og:description" :content="$t('page.how-far.description')" />
    </Head>
    <AdUnit unit="1" />
    <h1>
      <router-link :to="pathForLocale({ path: '/how-far-is-it' })">{{
        $t("how-far-title")
      }}</router-link>
    </h1>
    <h2 class="subtitle">{{ $t("how-far-subtitle") }}</h2>
    <div class="section section--distance">
      <div
        v-for="(location, index) in locations"
        :key="location ? location.name : index"
        class="distance-section"
        data-to="to"
      >
        <HowFarLocation v-model="locations[index]" />
        <div
          class="distance"
          v-if="index != 0 && segmentDistances.length >= index"
        >
          <h3 class="distance__label">Distance</h3>
          <div class="distance__value">
            {{ segmentDistancesFormatted[index - 1] }}
          </div>
          <select class="distance__unit" v-model="formatIndex">
            <option
              v-for="(format, index) in formats"
              :key="index"
              :value="index"
            >
              {{ format.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="distance" v-if="segmentDistances.length > 1">
      <h3 class="distance__label">Total Distance</h3>
      <i18n-t keypath="location-format" tag="div" class="distance__value">
        <template v-slot:value>{{ numberFormatted(totalDistance) }}</template>
        <template v-slot:units>{{ selectedFormat.shortName }}</template>
      </i18n-t>
      <select class="distance__unit" v-model="formatIndex">
        <option v-for="(format, index) in formats" :key="index" :value="index">
          {{ format.name }}
        </option>
      </select>
    </div>
    <div
      v-if="
        segmentDistances.length > 0 && locations[locations.length - 1] !== null
      "
      class="section"
    >
      <a class="button button--add" @click="addLocation()">Add location</a>
    </div>
    <HowFarMap
      v-if="points.length > 0"
      :locations="points"
      :locationDistances="segmentDistancesFormatted"
    />
    <TheFooter
      :far="false"
      :elevation="true"
      :title="$t('page.how-far.heading')"
    >
      <div class="section section--about">
        <div class="text-block" v-html="$t('page.how-far.html')"></div>
      </div>
      <template v-slot:nav>
        <li>
          <router-link :to="pathForLocale({ path: '/how-far-is-it' })">{{
            $t("how-far-title")
          }}</router-link>
        </li>
      </template>
    </TheFooter>
  </div>
</template>
<script>
import { pathForLocale, numberFormatted } from "@/helpers";
import TheFooter from "@/components/TheFooter.vue";
import AdUnit from "@/components/AdUnit.vue";
import HowFarLocation from "@/components/HowFarLocation.vue";
import { defineAsyncComponent } from "vue";
import { Head } from "@vueuse/head";

import { point } from "@turf/helpers";
import distance from "@turf/distance";

export default {
  components: {
    TheFooter,
    AdUnit,
    HowFarLocation,
    HowFarMap: defineAsyncComponent(() => import("@/components/HowFarMap.vue")),
    Head
  },
  data() {
    return {
      locations: [null, null],
      formatIndex: 3,
      formats: [
        {
          shortName: "ft",
          name: "Feet",
          multiplier: 3280.84
        },
        {
          shortName: "km",
          name: "Kilometers",
          multiplier: 1
        },
        {
          shortName: "m",
          name: "Meters",
          multiplier: 1000
        },
        {
          shortName: "mi",
          name: "Miles",
          multiplier: 0.621371
        }
      ]
    };
  },
  compatConfig: { COMPONENT_V_MODEL: false, WATCH_ARRAY: false },
  computed: {
    selectedFormat() {
      return this.formats[this.formatIndex];
    },
    points() {
      return this.locations
        .filter(location => !!location)
        .map(location => point(location.point.coordinates.slice().reverse()));
    },
    segmentDistances() {
      const distances = [];
      const { length } = this.points;
      if (length < 2) {
        return distances;
      }
      this.points.forEach((startPoint, index) => {
        const nextIndex = index + 1;
        if (nextIndex >= length) {
          return;
        }
        const endPoint = this.points[nextIndex];
        if (!!startPoint && !!endPoint) {
          distances.push(distance(startPoint, endPoint));
        }
      });
      return distances;
    },
    segmentDistancesFormatted() {
      return this.segmentDistances.map(segment =>
        this.$t("location-format", {
          value: this.numberFormatted(segment),
          units: this.selectedFormat.shortName
        })
      );
    },
    totalDistance() {
      return this.segmentDistances.reduce(
        (total, current) => total + current,
        0
      );
    }
  },
  methods: {
    addLocation() {
      this.locations.push(null);
    },
    pathForLocale,
    numberFormatted(num) {
      const numInFormat = num * this.selectedFormat.multiplier;
      return numberFormatted(numInFormat, {
        useFeet: false,
        locale: this.$t.locale,
        decimals: numInFormat > 10 ? 0 : 1
      });
    }
  },
  watch: {
    locations: {
      handler() {
        // if (this.locations[this.locations.length - 1] != null) {
        //   this.locations.push(null);
        // }
        // make sure there is never more than one empty box at the end of the array
        if (
          this.locations.length > 2 &&
          this.locations[this.locations.length - 2] === null
        ) {
          this.locations.pop();
        }
      },
      deep: true
    }
  }
};
</script>
